import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Heart, Landmark, CreditCard, Loader2, ExternalLink } from "lucide-react";

const frequencies = [
  { id: "unique", label: "Don unique", amounts: [10, 25, 50, 100, 250] },
  { id: "monthly", label: "Don mensuel", amounts: [5, 10, 20, 50, 100] },
  { id: "annual", label: "Don annuel", amounts: [50, 100, 250, 500, 1000] },
];
const programs = ["Un Enfant, Un Avenir","Un Repas, Un Sourire","Un Toit Pour Grandir","Cœur d'Enfant","Lumière sur l'Avenir","Là où les besoins sont les plus urgents"];

// Normalise et enrichit un lien PayPal — cf. AdminDashboard pour le format attendu.
function buildPaypalUrl(rawLink, amount, currency = "EUR") {
  if (!rawLink) return null;
  let url = String(rawLink).trim();
  if (!url) return null;
  if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
  try {
    const u = new URL(url);
    const isPaypalMe = /(^|\.)paypal\.me$/i.test(u.hostname);
    if (isPaypalMe && amount) {
      const segs = u.pathname.split("/").filter(Boolean);
      if (segs.length === 1) {
        u.pathname = `/${segs[0]}/${Math.round(amount)}${currency}`;
        return u.toString();
      }
    }
    return u.toString();
  } catch {
    return url;
  }
}

export default function Donate() {
  const [freq, setFreq] = useState("unique");
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [program, setProgram] = useState(programs[5]);
  const [method, setMethod] = useState("stripe");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [site, setSite] = useState({});
  const summaryRef = useRef(null);
  const { t } = useI18n();
  const cur = frequencies.find(f => f.id === freq);
  const suffix = freq === "unique" ? "" : (freq === "monthly" ? "/mois" : "/an");
  const paypalConfigured = Boolean(site.paypal_link);

  // Sur mobile, dès qu'une méthode de paiement est choisie, on fait défiler
  // automatiquement vers le formulaire donateur pour que l'utilisateur voie
  // le bouton "Payer sur PayPal" (le sidebar sticky n'est visible qu'en
  // desktop, en mobile il apparaît PLUS BAS que les méthodes).
  const handleSelectMethod = (m) => {
    setMethod(m);
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      setTimeout(() => {
        summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    }
  };

  useEffect(() => {
    api.get("/settings").then(r => setSite(r.data?.site || {})).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amt = custom ? Number(custom) : amount;
    if (!amt || amt < 1) { toast.error(t("donate.errInvalidAmount")); return; }
    setLoading(true);
    try {
      if (method === "stripe") {
        const r = await api.post("/payments/checkout", {
          donor_name: name, donor_email: email, amount: amt, frequency: freq, program,
          origin_url: window.location.origin, message,
        });
        if (r.data?.checkout_url) { window.location.href = r.data.checkout_url; return; }
        toast.error(t("donate.errStripe"));
        return;
      }
      if (method === "paypal") {
        if (!paypalConfigured) { toast.error("Le lien PayPal n'est pas encore configuré."); return; }
        const r = await api.post("/donations", { donor_name: name, donor_email: email, amount: amt, frequency: freq, program, payment_method: "paypal", message });
        const finalUrl = buildPaypalUrl(site.paypal_link, amt);
        if (!finalUrl || !r.data?.id || !r.data?.paypal_token) { toast.error("Impossible de préparer le paiement PayPal."); return; }
        const params = new URLSearchParams({ id: r.data.id, token: r.data.paypal_token, url: finalUrl, amount: String(amt), program });
        window.location.href = `/paiement/paypal?${params.toString()}`;
        return;
      }
      // Bank transfer
      await api.post("/donations", { donor_name: name, donor_email: email, amount: amt, frequency: freq, program, payment_method: "bank", message });
      window.location.href = "/merci";
    } catch (err) { toast.error(err?.response?.data?.detail || t("donate.errGeneric")); }
    finally { setLoading(false); }
  };

  const methodNote = () => {
    if (method === "stripe") return t("donate.stripeNote");
    if (method === "paypal") return <>Vous serez redirigé vers PayPal, puis vous pourrez récupérer votre reçu fiscal automatiquement. <ExternalLink size={10} className="inline mb-0.5"/></>;
    return t("donate.noStoreNote");
  };
  const submitLabel = () => {
    if (loading) return <><Loader2 className="animate-spin" size={18}/> {t("donate.processing")}</>;
    if (method === "stripe") return <>{t("donate.payNow")}</>;
    if (method === "paypal") return <>❤ Payer sur PayPal <ExternalLink size={14}/></>;
    return <>❤ {t("donate.confirmDonate")}</>;
  };

  return (
    <div data-testid="donate-page">
      <section className="hero-gradient py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-warm-orange font-semibold">{t("pages.donate.tag")}</div>
          <h1 className="font-display text-deep-blue text-5xl sm:text-6xl leading-tight mt-3">{t("pages.donate.t1")}<br/><span className="italic text-warm-orange">{t("pages.donate.t2")}</span></h1>
          <p className="text-deep-blue/70 mt-6 max-w-2xl">{t("pages.donate.sub")}</p>
        </div>
      </section>
      <section className="py-16 bg-ivory">
        <form onSubmit={submit} className="max-w-5xl mx-auto px-5 lg:px-10 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-warm-orange mb-3">{t("donate.freqLabel")}</div>
              <div className="grid grid-cols-3 gap-2">
                {frequencies.map(f => (
                  <button type="button" key={f.id} onClick={() => { setFreq(f.id); setAmount(f.amounts[2]); setCustom(""); }}
                    className={`p-4 rounded-xl border-2 transition ${freq===f.id?"border-gold bg-gold/10":"border-black/5 hover:border-gold/40"}`} data-testid={`freq-${f.id}`}>
                    <div className="font-display text-lg text-deep-blue">{t(`donate.freq_${f.id}`)}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-warm-orange mb-3">{t("donate.amountLabel")}</div>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                {cur.amounts.map(a => (
                  <button type="button" key={a} onClick={() => { setAmount(a); setCustom(""); }}
                    className={`p-4 rounded-xl border-2 font-semibold transition ${!custom && amount===a ? "border-gold bg-gold text-deep-blue":"border-black/5 text-deep-blue hover:border-gold/40"}`} data-testid={`amt-${a}`}>
                    {a}€{suffix}
                  </button>
                ))}
              </div>
              <input type="number" min="1" placeholder={t("donate.customAmount")} value={custom} onChange={e => setCustom(e.target.value)}
                className="mt-3 w-full p-4 rounded-xl border-2 border-black/5 focus:border-gold outline-none" data-testid="amt-custom" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-warm-orange mb-3">{t("donate.programLabel")}</div>
              <select value={program} onChange={e=>setProgram(e.target.value)} className="w-full p-4 rounded-xl border-2 border-black/5 focus:border-gold outline-none" data-testid="donate-program">
                {programs.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-warm-orange mb-3">{t("donate.methodLabel")}</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <button type="button" onClick={()=>handleSelectMethod("stripe")} className={`p-4 rounded-xl border-2 flex items-center gap-3 ${method==="stripe"?"border-gold bg-gold/10":"border-black/5"}`} data-testid="pay-stripe">
                  <CreditCard className="text-gold"/><span className="text-deep-blue font-semibold text-left leading-tight">{t("donate.methodCard")}<span className="block text-xs text-deep-blue/60 font-normal">{t("donate.methodCardSub")}</span></span>
                </button>
                <button type="button" onClick={()=>handleSelectMethod("paypal")} disabled={!paypalConfigured}
                  title={paypalConfigured ? "" : "Le lien PayPal n'est pas encore configuré"}
                  className={`p-4 rounded-xl border-2 flex items-center gap-3 ${method==="paypal"?"border-gold bg-gold/10":"border-black/5"} ${paypalConfigured ? "" : "opacity-50 cursor-not-allowed"}`} data-testid="pay-paypal">
                  <Heart className="text-warm-orange"/><span className="text-deep-blue font-semibold">PayPal{paypalConfigured ? "" : " (bientôt)"}</span>
                </button>
                <button type="button" onClick={()=>handleSelectMethod("bank")} className={`p-4 rounded-xl border-2 flex items-center gap-3 ${method==="bank"?"border-gold bg-gold/10":"border-black/5"}`} data-testid="pay-bank">
                  <Landmark className="text-deep-blue"/><span className="text-deep-blue font-semibold">{t("donate.methodBank")}</span>
                </button>
              </div>
              {/* QR code PayPal : les donateurs sur ordinateur peuvent le scanner avec leur téléphone pour payer plus vite. */}
              {paypalConfigured && method === "paypal" && (
                <div className="mt-4 p-4 rounded-xl bg-white border-2 border-gold/40 flex flex-col sm:flex-row items-center gap-4" data-testid="paypal-qr-block">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=10&data=${encodeURIComponent(buildPaypalUrl(site.paypal_link, custom ? Number(custom) : amount) || site.paypal_link)}`}
                    alt="QR code pour don PayPal"
                    width="140" height="140"
                    className="rounded-lg flex-shrink-0 bg-white"
                    data-testid="paypal-qr-image"
                  />
                  <div className="text-sm text-deep-blue/80 text-center sm:text-left">
                    <div className="font-display text-lg text-deep-blue font-semibold">📱 Payer en scannant</div>
                    <p className="mt-1">Scannez ce QR code avec l'appareil photo de votre téléphone pour ouvrir PayPal directement, avec le montant <b>{custom || amount} €</b> pré-rempli.</p>
                  </div>
                </div>
              )}
              <p className="text-xs text-deep-blue/60 mt-2">{methodNote()}</p>
              {/* Rappel mobile : sur écran étroit le bouton final est plus bas, on ajoute une flèche pour guider l'utilisateur. */}
              <div className="lg:hidden mt-4 p-4 rounded-xl bg-gold/15 border-2 border-gold/40 text-center animate-pulse" data-testid="mobile-continue-hint">
                <p className="text-sm text-deep-blue font-semibold">
                  ↓ Remplissez votre nom et email ci-dessous, puis cliquez sur <b className="text-warm-orange">
                    {method === "paypal" ? "« ❤ Payer sur PayPal »" : method === "stripe" ? "« Payer maintenant »" : "« ❤ Confirmer le don »"}
                  </b>
                </p>
              </div>
            </div>
          </div>
          <div ref={summaryRef} className="lg:col-span-2 space-y-4 p-6 rounded-2xl bg-deep-blue text-ivory h-fit lg:sticky lg:top-24">
            <div className="text-xs uppercase tracking-widest text-gold">{t("donate.yourInfo")}</div>
            <input required placeholder={t("donate.fullName")} value={name} onChange={e=>setName(e.target.value)} className="w-full p-3 rounded-lg bg-white/10 border border-white/10 placeholder-ivory/40 text-ivory outline-none focus:border-gold" data-testid="donor-name"/>
            <input required type="email" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} className="w-full p-3 rounded-lg bg-white/10 border border-white/10 placeholder-ivory/40 text-ivory outline-none focus:border-gold" data-testid="donor-email"/>
            <textarea placeholder={t("donate.messageOpt")} value={message} onChange={e=>setMessage(e.target.value)} rows={3} className="w-full p-3 rounded-lg bg-white/10 border border-white/10 placeholder-ivory/40 text-ivory outline-none focus:border-gold" data-testid="donor-msg"/>
            <div className="p-4 rounded-lg bg-gold/10 border border-gold/30">
              <div className="text-xs text-ivory/70">{t("donate.summary")}</div>
              <div className="font-display text-3xl text-gold mt-1">{custom || amount}€{suffix}</div>
              <div className="text-xs text-ivory/60 mt-1">{program}</div>
            </div>
            <button type="submit" disabled={loading} className="btn-gold w-full py-4 rounded-full font-semibold disabled:opacity-60 flex items-center justify-center gap-2" data-testid="donate-submit">
              {submitLabel()}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
